import { useState } from 'react';
import { C, SectionLabel, wrap } from './styles';
import { P, H2b, UL, LI } from './content/Typography';
import { useDocumentMeta } from './content/useDocumentMeta';
import { useNoindex } from './content/useNoindex';
import Nav from './components/Nav';
import Footer from './components/Footer';

const CONTACT_EMAIL = 'quotes@getmetalmatch.com';

const DISCLAIMER = 'These renderings are AI-generated concept visualizations showing approximate appearance. They are not engineering drawings. Final dimensions, specifications, and manufacturing details will be confirmed by the manufacturer during the quoting process.';

const PAGE_TITLE = 'Approve Your Concept Renderings | MetalMatch';
const PAGE_DESCRIPTION = 'Review your AI-generated concept renderings, approve them to send to fabricators, or request changes.';

function buildMailto(subject, body) {
  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function NoteOption({ color, label, subject, placeholder, sendLabel, bodyPrefix }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');

  return (
    <div style={{ background: C.surf, border: `1px solid ${C.bdr}`, borderRadius: 10, padding: 22 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', border: 'none', borderRadius: 6, padding: '14px 20px',
          background: color, color: '#fff',
          fontFamily: "'Instrument Sans', sans-serif", fontSize: 14, fontWeight: 600,
          letterSpacing: '0.02em', transition: 'opacity 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = 0.88}
        onMouseLeave={e => e.currentTarget.style.opacity = 1}
      >{label}</button>

      {open && (
        <div style={{ marginTop: 16 }}>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={placeholder}
            style={{ minHeight: 96, resize: 'vertical', marginBottom: 12 }}
          />
          <a
            href={buildMailto(subject, `${bodyPrefix}\n\n${note || '(No additional notes provided.)'}`)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontFamily: "'Instrument Sans', sans-serif", fontSize: 13, fontWeight: 600,
              padding: '11px 22px', borderRadius: 5, border: `1.5px solid ${color}`,
              color, textDecoration: 'none',
            }}
          >{sendLabel}</a>
        </div>
      )}
    </div>
  );
}

export default function Approve() {
  useDocumentMeta(PAGE_TITLE, PAGE_DESCRIPTION);
  useNoindex();

  const approveHref = buildMailto(
    'MetalMatch Approval: Approved',
    'I have reviewed the concept renderings and approve them as-is. Please proceed and send this project to your matched fabricators.'
  );

  return (
    <>
      <Nav />

      <section style={{ padding: '64px 0 20px', borderBottom: `1px solid ${C.bdr}` }}>
        <div style={wrap}>
          <SectionLabel orange>Project review</SectionLabel>
          <h1 style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: 'clamp(30px, 5vw, 48px)',
            fontWeight: 700, lineHeight: 1.08,
            textTransform: 'uppercase', color: C.txt,
            maxWidth: 720, marginBottom: 16,
          }}>Your AI Concept Renderings Are Ready</h1>
          <p style={{ color: C.mut, fontSize: 16, lineHeight: 1.7, maxWidth: 620 }}>
            Take a look at the renderings we sent for your project, then choose how you'd like to proceed below.
          </p>
        </div>
      </section>

      <section style={{ padding: '40px 0 0' }}>
        <div style={{ ...wrap, maxWidth: 760 }}>
          <div style={{
            background: C.navybg, border: `1px solid rgba(50,151,219,0.35)`,
            borderRadius: 8, padding: '18px 22px',
          }}>
            <div style={{ fontSize: 10, color: C.navy, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>
              Please note
            </div>
            <p style={{ fontSize: 13.5, color: C.mut, lineHeight: 1.75 }}>{DISCLAIMER}</p>
          </div>
        </div>
      </section>

      <section style={{ padding: '40px 0 8px' }}>
        <div style={{ ...wrap, maxWidth: 760 }}>
          <H2b>What happens next</H2b>
          <P>Once you've reviewed your renderings, choose one of three options below:</P>
          <UL>
            <LI><strong>Approve</strong> — your brief is finalized and sent to 2–3 matched Ontario fabricators for competitive quoting.</LI>
            <LI><strong>Request changes</strong> — tell us what needs to be adjusted and we'll revise the renderings and send an updated version.</LI>
            <LI><strong>This isn't right</strong> — let us know if the concept missed the mark, and our team will follow up to reset the brief.</LI>
          </UL>
        </div>
      </section>

      <section style={{ padding: '24px 0 60px' }}>
        <div style={{ ...wrap, maxWidth: 760, display: 'flex', flexDirection: 'column', gap: 14 }}>

          <div style={{ background: C.surf, border: `1px solid ${C.bdr}`, borderRadius: 10, padding: 22 }}>
            <a
              href={approveHref}
              style={{
                display: 'block', textAlign: 'center', textDecoration: 'none',
                border: 'none', borderRadius: 6, padding: '14px 20px',
                background: '#1baf7a', color: '#fff',
                fontFamily: "'Instrument Sans', sans-serif", fontSize: 14, fontWeight: 600,
                letterSpacing: '0.02em', transition: 'opacity 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = 0.88}
              onMouseLeave={e => e.currentTarget.style.opacity = 1}
            >Approve — Send to Fabricators</a>
          </div>

          <NoteOption
            color="#eda100"
            label="Request Changes"
            subject="MetalMatch Approval: Changes"
            placeholder="Describe what you'd like adjusted — dimensions, material, finish, or anything else."
            sendLabel="Send Revision Request →"
            bodyPrefix="I'd like to request the following changes to the concept renderings:"
          />

          <NoteOption
            color="#6b7280"
            label="This Isn't Right"
            subject="MetalMatch Approval: Redo"
            placeholder="Let us know what's off about the concept so we can take a different approach."
            sendLabel="Send Feedback →"
            bodyPrefix="The concept renderings don't match what I had in mind. Here's some context:"
          />

        </div>
      </section>

      <section style={{ padding: '0 0 80px' }}>
        <div style={wrap}>
          <p style={{ fontSize: 13, color: C.dim, textAlign: 'center' }}>
            Questions? Reply to the email we sent or contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: C.orange, textDecoration: 'none' }}>{CONTACT_EMAIL}</a>.
          </p>
        </div>
      </section>

      <Footer />
    </>
  );
}
